import { yupResolver } from "@hookform/resolvers/yup";
import { BasicDetailsForm } from "entities/RequestBasicDetails";
import constants from "entities/RequestBasicDetails/constants";
import { getBasicDetailsValidationSchema } from "features/EditRequestBasicDetails/lib/validations/basicDetailsSchema";
import { memo, useCallback, useEffect, useState, useReducer } from "react";
import { Stack } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api, { baseApiService } from "shared/api";
import { useUnsavedChangesPrompt } from "shared/hooks/useUnsavedChangesPrompt";
import { Button } from "shared/ui";
import { withToast } from "shared/utils";
import { toast } from "react-toastify";
import { SuccessfulSubmitCard } from "shared/ui";
import { BasicDetailsContainer } from "widgets/Request";
import { REQUEST_TYPES, ROLES, ROUTES } from "shared/config";
import { UploadToOCR } from "features/OCR";
import {
  useAdditionalAcceptorField,
  useAdvancePaymentFieldController,
} from "features/EditRequestBasicDetails";

const { defaultValues, additionalAcceptanceFieldStateDefaultValues } =
  constants.basicDetails;

const loadingStateDefaultValues = {
  isCreating: false,
  isSubmitting: false,
};

function SubmitRequestBasicDetails({
  user,
  requestType,
  formHeader,
  requestorFullName,
  representations = [],
  validations,
  dependencies,
  withOCR = false,
}) {
  const { t } = useTranslation("RequestBasicDetails");
  const navigate = useNavigate();
  const [loadingState, updateLoadingState] = useReducer(
    (prev, next) => ({ ...prev, ...next }),
    loadingStateDefaultValues
  );
  const form = useForm({
    defaultValues,
    resolver: yupResolver(
      getBasicDetailsValidationSchema(
        validations?.advance_payment_rules?.minimum_advance_amount
      )
    ),
  });
  const [isUploadingFilesToOCR, setIsUploadingFilesToOCR] = useState(false);

  const [requestState, setRequestState] = useState({
    submitted: false,
    number: null,
  });

  const {
    setValue,
    getValues,
    reset,
    watch,
    control,
    formState: { isDirty },
  } = form;

  const representation = watch("representation_id");

  useEffect(() => {
    setValue("supervisor_email", representation?.manager_email, {
      shouldDirty: true,
    });
    setValue("cost_centre", representation?.cost_centre, {
      shouldDirty: true,
    });
  }, [representation]);

  useUnsavedChangesPrompt({
    showPrompt: isDirty && !requestState.submitted,
    stepLinks: [
      requestType === REQUEST_TYPES.EXPENSE && getValues("advance_payment")
        ? ROUTES.request.user_queue
        : ROUTES.request.details_basename,
    ],
  });

  const resetForm = () => {
    setAdditionalAcceptorFieldState(
      additionalAcceptanceFieldStateDefaultValues
    );
    setAdvancePayment(false);

    reset({
      ...defaultValues,
      supervisor_email: representations[0]?.manager_email,
      cost_centre: user.cost_centre,
      representation_id: representations[0],
      type: requestType,
    });
  };

  useEffect(() => {
    resetForm();
  }, []);

  const [advancePayment, setAdvancePayment] = useState(false);

  const toggleAdvancePayment = useCallback(() => {
    setAdvancePayment(!advancePayment);
  }, [advancePayment]);

  const requestorEmail = representation?.search_string;

  const handleSubmit = useCallback(
    ({ representation_id, advance_payment, ...formData }) => {
      // @ts-ignore
      updateLoadingState({ isCreating: true });
      withToast(() =>
        api.post(baseApiService.ENDPOINTS.request.shared.create, {
          representation_id: representation_id?.value,
          // Back requires amount as a string ¯\_(ツ)_/¯
          advance_payment:
            typeof advance_payment === "number"
              ? advance_payment.toString()
              : null,
          ...formData,
        })
      )
        .then((response) => {
          if (response?.status === 200) {
            if (
              requestType === REQUEST_TYPES.EXPENSE &&
              advance_payment != null
            ) {
              // @ts-ignore
              updateLoadingState({ isSubmitting: true });
              const requestNumber = response?.data?.data?.number;

              api
                .post(baseApiService.ENDPOINTS.request.shared.submit, {
                  application_id: response?.data?.data?.id,
                  user_role: ROLES.USER,
                })
                .then((response) => {
                  toast.dismiss();
                  toast.success(t("toasts.request_submitted"), {
                    theme: "colored",
                  });

                  if (response?.status === 200) {
                    setRequestState({
                      submitted: true,
                      number: requestNumber,
                    });
                  }
                })
                .catch((error) => {
                  if (error?.response?.status === 422) {
                    toast.error(
                      error.response?.data?.message ?? error.message,
                      {
                        theme: "colored",
                        autoClose: 10000,
                      }
                    );
                  } else {
                    toast.error(
                      error.response?.data?.message ?? error.message,
                      {
                        theme: "colored",
                      }
                    );
                  }
                })
                // @ts-ignore
                .finally(() => updateLoadingState({ isSubmitting: false }));
            } else {
              navigate(
                `${ROUTES.request.details_basename}/${response.data.data.number}`
              );
            }
          }
        })
        // @ts-ignore
        .finally(() => updateLoadingState({ isCreating: false }));
    },
    []
  );

  const {
    additionalAcceptorFieldState,
    setAdditionalAcceptorFieldState,
    toggleAdditionalAcceptorField,
  } = useAdditionalAcceptorField(
    form,
    dependencies,
    advancePayment,
    requestorEmail
  );

  useAdvancePaymentFieldController(
    form,
    validations,
    dependencies,
    advancePayment,
    setAdditionalAcceptorFieldState,
    requestorEmail,
    representation
  );

  if (requestState.submitted) {
    return <SuccessfulSubmitCard requestNumber={requestState.number} />;
  }

  return (
    <BasicDetailsContainer>
      <BasicDetailsForm
        form={form}
        formHeader={formHeader}
        validations={validations}
        advancePaymentCurrencyCode={
          validations?.advance_payment_rules?.currency_code
        }
        handleSubmit={handleSubmit}
        representations={representations}
        requestorFullName={requestorFullName}
        toggleAdditionalAcceptorField={toggleAdditionalAcceptorField}
        additionalAcceptance={additionalAcceptorFieldState.open}
        lockAdditionalAcceptance={additionalAcceptorFieldState.disabled}
        toggleAdvancePayment={toggleAdvancePayment}
        advancePayment={advancePayment}
        displayAdvancePayment={user?.is_advance_payment_allowed}
        isSupervisorDisabled={advancePayment}
        ocr={
          withOCR && !advancePayment ? (
            <UploadToOCR
              control={control}
              setValue={setValue}
              validations={validations}
              setIsUploadingFilesToOCR={setIsUploadingFilesToOCR}
            />
          ) : null
        }
        formControls={
          <Stack direction="horizontal" className="justify-content-between">
            <Button variant="outline-primary" onClick={resetForm}>
              {t("buttons.clear_form")}
            </Button>
            <Button
              type="submit"
              isLoading={loadingState.isCreating || loadingState.isSubmitting}
              disabled={
                loadingState.isCreating ||
                loadingState.isSubmitting ||
                isUploadingFilesToOCR
              }
            >
              {advancePayment && requestType === REQUEST_TYPES.EXPENSE
                ? t("buttons.submit")
                : t("buttons.next_step")}
            </Button>
          </Stack>
        }
      />
    </BasicDetailsContainer>
  );
}

export default memo(SubmitRequestBasicDetails);
